import { UnpackAnything } from '@/utils/type';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Stack,
  Link,
  CircularProgress,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import useTheme from '@/theme/useTheme';
import { useTranslation } from 'next-i18next';
import {
  IPromotionCardParams,
  IPromotionQueryReqDto,
  getPromotionList,
} from '@/apis/promotions';
import dayjs from 'dayjs';

import { GrantForEveryDeposit } from './GrantForEventDeposit';

import { StyledToggleButtonGroup, BoxChip, StyledToggleButton } from './styles';
import { useRouter } from 'next/router';
import LazyLoadedResponsiveImage from '@/components/ui/LazyLoadedResponsiveImage';
import { SimplePagination } from '@/components/ui/SimplePagination';
import WrappedButton from '@/components/ui/WrappedButton';
import { specialPromotions } from '@/components/features/wallet/WalletWidget/WalletModal/BonusItem';
import { formatDate } from '@/utils/time';
import useWalletModal from '@/hooks/biz/useWalletModal';

export function isPromotionActive(endTime: string) {
  if (!endTime) return true;
  return new Date(endTime).getTime() - new Date().getTime() > 0;
}

const Wrapper = styled(Stack)(({ theme }) => ({
  margin: '0 auto',
  padding: theme.spacing(2.5),
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
  ['ol, ul']: {
    paddingInlineStart: '1em',
  },
  ['a']: {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
}));

const PromotionsListView: React.FC = () => {
  const [t] = useTranslation('promotion');
  const PromitionsStatuses = [
    {
      label: t('Latest Promotions'),
      value: 'active',
    },
    // {
    //   label: t('Archived'),
    //   value: 'finished',
    // },
  ];
  const [list, setList] = useState<Array<IPromotionCardParams>>([]);
  const [status, setStatus] = useState(PromitionsStatuses[0].value);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();
  const router = useRouter();

  const openWallet = useWalletModal();

  const PAGE_SIZE = 9;
  const [pagination, setPagination] = useState({
    page: 0,
    totalPageSize: 1,
  });

  function handleChangeStatus(e: any) {
    setStatus(e.target.value);
    getPromotions({
      activity: e.target.value === 'active',
      page: 0,
    });
  }

  function getPromotions(params: IPromotionQueryReqDto) {
    setLoading(true);
    getPromotionList({
      ...params,
      pageSize: PAGE_SIZE,
    })
      .then((data) => {
        setList(data.promotionDTOList.concat(specialPromotions));
        setPagination({
          page: params.page,
          totalPageSize: data.totalPageSize,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }

  async function handlePageChange({ newPage, action }: any) {
    getPromotions({
      page: newPage,
    });
  }

  useEffect(() => {
    getPromotions({
      page: 0,
    });
  }, []);

  return (
    <Wrapper
      sx={{
        backgroundColor: theme.palette.bg.main,
      }}
    >
      <GrantForEveryDeposit />
      <Box>
        <Stack
          sx={{
            borderRadius: theme.spacing(1),
            paddingY: theme.spacing(2.5),
            marginY: theme.spacing(2.5),
          }}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <StyledToggleButtonGroup
            value={status}
            exclusive
            onChange={handleChangeStatus}
            aria-label={t('promotion types')}
          >
            {PromitionsStatuses.map((item) => {
              return (
                <StyledToggleButton
                  key={item.value}
                  value={item.value}
                  aria-label={item.label}
                >
                  {item.label}
                </StyledToggleButton>
              );
            })}
          </StyledToggleButtonGroup>
        </Stack>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Grid container spacing={1}>
            {list.map((item) => (
              <Grid key={item.id} item xs={12} sm={12} md={4}>
                <Card
                  component={'div'}
                  sx={{
                    borderRadius: theme.spacing(1),
                    paddingY: theme.spacing(1.5),
                    background: 'transparent',
                    cursor: 'pointer',
                    boxShadow: 'none',
                  }}
                  onClick={() => router.push(`/promotions/${item.slug}`)}
                >
                  <Box
                    sx={{
                      height: '140px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover .moon-promotion-bonus-list-item': {
                        display: {
                          xs: 'none',
                          sm: 'flex',
                        },
                      },
                    }}
                  >
                    <LazyLoadedResponsiveImage
                      sources={[{ srcSet: item.cardImage, media: '' }]}
                      src={item.cardImage}
                      alt={item.title}
                    />
                    {item.isBonus ? (
                      <Box
                        className="moon-promotion-bonus-list-item"
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          width: '100%',
                          display: 'none',
                          justifyContent: 'center',
                          padding: '10px',
                          background: 'rgba(00, 00, 00, 0.70)',
                        }}
                      >
                        <WrappedButton
                          sx={{ mr: 2 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openWallet();
                          }}
                        >
                          {t('Get Bonus')}
                        </WrappedButton>
                        <WrappedButton variant="outlined">
                          {t('More')}
                        </WrappedButton>
                      </Box>
                    ) : (
                      <></>
                    )}
                  </Box>
                  <CardContent sx={{ px: 1, position: 'relative' }}>
                    <Link
                      href={`/promotions/${item.id}`}
                      sx={{
                        height: 0,
                      }}
                    />

                    <Typography
                      gutterBottom
                      color="text.secondary"
                      variant="body2"
                      fontSize={'12px'}
                      fontWeight={400}
                      component="div"
                    >
                      {item.endTime
                        ? `Finishes on ${formatDate(item.endTime)}`
                        : 'Permanent'}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontSize={'16px'}
                      fontWeight={500}
                      color="text.primary"
                    >
                      {item.title}
                    </Typography>

                    {isPromotionActive(item.endTime) ? (
                      <BoxChip
                        sx={{
                          position: 'absolute',
                          right: '0',
                          top: '16px',
                        }}
                        label={t('In Progress')}
                      />
                    ) : (
                      <></>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      <SimplePagination
        loading={loading}
        currentPage={pagination.page}
        totalPage={pagination.totalPageSize}
        onChange={handlePageChange}
      />
    </Wrapper>
  );
};

export default PromotionsListView;
